/*
 * /login — dedicated sign-in / sign-up page.
 *
 * Complements the header AuthModal for people who bookmark /login or get
 * redirected here from a protected route (/admin, /account) while logged out.
 *
 * ?next=/some/path is honoured after successful login (defaults to /account
 * for customers, /admin for admins).
 */
import React, { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Mail, Lock, User, Phone } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";

function safeNext(raw) {
  // Only allow same-origin, absolute-path redirects. Prevents open-redirect abuse.
  if (!raw || typeof raw !== "string") return null;
  if (!raw.startsWith("/") || raw.startsWith("//")) return null;
  return raw;
}

export default function Login() {
  const { isAuthenticated, isAdmin, loading, login, register } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const nextPath = safeNext(params.get("next"));
  const initialMode = params.get("mode") === "register" ? "register" : "login";

  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);

  const isLogin = mode === "login";

  // Redirect away once authenticated.
  useEffect(() => {
    if (loading || !isAuthenticated) return;
    const target = nextPath || (isAdmin ? "/admin" : "/account");
    navigate(target, { replace: true });
  }, [loading, isAuthenticated, isAdmin, nextPath, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--mm-bg)] flex items-center justify-center">
        <Loader2 size={32} className="text-[var(--mm-accent)] animate-spin" />
      </div>
    );
  }
  if (isAuthenticated) {
    // useEffect will navigate; render null to avoid flashing the form.
    return <Navigate to={nextPath || (isAdmin ? "/admin" : "/account")} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!isLogin && !name.trim()) {
      toast({ title: "Missing name", description: "Please enter your name." });
      return;
    }
    if (!email.trim() || !/^\S+@\S+\.\S+$/.test(email.trim())) {
      toast({ title: "Invalid email", description: "Please enter a valid email address." });
      return;
    }
    if (!password || password.length < 6) {
      toast({ title: "Password too short", description: "Password must be at least 6 characters." });
      return;
    }
    setBusy(true);
    try {
      const user = isLogin
        ? await login(email.trim(), password)
        : await register({
            email: email.trim(),
            password,
            name: name.trim(),
            phone: phone.trim() || undefined,
          });
      toast({
        title: isLogin ? `Welcome back, ${user.name || user.email}` : `Welcome, ${user.name}`,
        description: isLogin ? "You are now signed in." : "Your account was created.",
      });
      // Redirect handled by the useEffect above once state updates.
    } catch (err) {
      toast({
        title: isLogin ? "Sign in failed" : "Sign up failed",
        description: err?.response?.data?.detail || "Please try again.",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--mm-bg)] grain flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-[var(--mm-cream-muted)] hover:text-[var(--mm-accent)] transition-colors mb-8"
          data-testid="login-back-home"
        >
          <ArrowLeft size={14} /> Back to Mamamina&apos;s
        </Link>

        <div className="mb-8">
          <div className="section-label mb-3">{isLogin ? "Sign In" : "Create Account"}</div>
          <h1 className="font-display italic text-4xl md:text-5xl text-[var(--mm-cream)] leading-[1.05]">
            {isLogin ? "Welcome back." : "Join the family."}
          </h1>
          {nextPath && (
            <p className="text-[var(--mm-cream-muted)] text-sm mt-3 font-light">
              Sign in to continue to <span className="text-[var(--mm-accent)]">{nextPath}</span>
            </p>
          )}
        </div>

        <div className="border border-[var(--mm-line)] bg-[var(--mm-bg-alt)] p-6 md:p-8">
          <form onSubmit={submit} noValidate className="space-y-4">
            {!isLogin && (
              <label className="block">
                <div className="flex items-center gap-2 mb-1.5 text-xs tracking-[0.2em] uppercase text-[var(--mm-cream-muted)]">
                  <User size={12} /> Name
                </div>
                <input
                  data-testid="login-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your full name"
                  className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] placeholder:text-[var(--mm-cream-muted)]/60 px-4 py-3 focus:border-[var(--mm-accent)] outline-none"
                />
              </label>
            )}

            <label className="block">
              <div className="flex items-center gap-2 mb-1.5 text-xs tracking-[0.2em] uppercase text-[var(--mm-cream-muted)]">
                <Mail size={12} /> Email
              </div>
              <input
                data-testid="login-email"
                type="text"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] placeholder:text-[var(--mm-cream-muted)]/60 px-4 py-3 focus:border-[var(--mm-accent)] outline-none"
              />
            </label>

            {!isLogin && (
              <label className="block">
                <div className="flex items-center gap-2 mb-1.5 text-xs tracking-[0.2em] uppercase text-[var(--mm-cream-muted)]">
                  <Phone size={12} /> Phone (optional)
                </div>
                <input
                  data-testid="login-phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="04xx xxx xxx"
                  className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] placeholder:text-[var(--mm-cream-muted)]/60 px-4 py-3 focus:border-[var(--mm-accent)] outline-none"
                />
              </label>
            )}

            <label className="block">
              <div className="flex items-center gap-2 mb-1.5 text-xs tracking-[0.2em] uppercase text-[var(--mm-cream-muted)]">
                <Lock size={12} /> Password
              </div>
              <input
                data-testid="login-password"
                type="password"
                autoComplete={isLogin ? "current-password" : "new-password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={isLogin ? "Your password" : "At least 6 characters"}
                className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] placeholder:text-[var(--mm-cream-muted)]/60 px-4 py-3 focus:border-[var(--mm-accent)] outline-none"
              />
            </label>

            <button
              type="submit"
              data-testid="login-submit"
              disabled={busy}
              className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {busy && <Loader2 size={14} className="animate-spin" />}
              {busy ? "Please wait..." : isLogin ? "Sign In" : "Create Account"}
            </button>

            <button
              type="button"
              onClick={() => setMode(isLogin ? "register" : "login")}
              data-testid="login-toggle-mode"
              className="w-full text-center text-xs tracking-[0.22em] uppercase text-[var(--mm-cream-muted)] hover:text-[var(--mm-accent)] transition-colors mt-2"
            >
              {isLogin ? "New here? Create an account" : "Already have an account? Sign in"}
            </button>

            <div className="flex items-center gap-3 my-3">
              <span className="h-px flex-1 bg-[var(--mm-line)]" />
              <span className="text-[0.62rem] tracking-[0.24em] uppercase text-[var(--mm-cream-muted)]">or</span>
              <span className="h-px flex-1 bg-[var(--mm-line)]" />
            </div>

            <button
              type="button"
              data-testid="login-google-btn"
              onClick={() => {
                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                const redirectUrl = window.location.origin + "/account";
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              className="btn-outline w-full flex items-center justify-center gap-2"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M21.35 11.1H12v2.9h5.35c-.5 2.5-2.6 4.3-5.35 4.3a5.8 5.8 0 1 1 0-11.6c1.48 0 2.82.56 3.85 1.48l2.17-2.17A8.9 8.9 0 0 0 12 3.1a8.9 8.9 0 1 0 0 17.8c5.14 0 8.85-3.61 8.85-8.7 0-.38-.04-.75-.1-1.1Z"/></svg>
              Continue with Google
            </button>
          </form>
        </div>

        <p className="text-center text-[0.62rem] tracking-[0.24em] uppercase text-[var(--mm-cream-muted)]/60 mt-8">
          Staff PIN login is at <Link to="/staff" className="hover:text-[var(--mm-accent)] transition-colors">/staff</Link>
        </p>
      </div>
    </div>
  );
}
